/**
 * Settings — this door phone's own preferences, mounted as a schmancy
 * `show()` overlay from the gear icon in scan.view.ts's own chrome. The
 * overlay owns dismissal (Esc, backdrop click, or this view's own close
 * button dispatching `CustomEvent('close', …)`) — there is no back button
 * and no route. Preferences take effect immediately. Changing event closes the overlay;
 * sign-out keeps its clicked action busy until its correlated answer.
 *
 * Every field earned its place against a named failure: the type filter
 * exists because one venue runs several doors with different entitlements
 * and a General Admission ticket at the VIP entrance must render wrong-type
 * rather than being redeemed; the provenance filter exists because that same
 * venue runs a separate guest entrance; auto/manual exists because auto mode
 * has no undo and a stray decode destroys data; sound/haptics are each a
 * strict on/off pair, never a volume slider — the prior system's admit tone
 * drifted to an inaudible 0.1 through exactly that lever, and its rejection
 * tone was silent outright. Both filter rows hide entirely when this event
 * carries fewer than two of whatever that row distinguishes between — ticket
 * types for one, ticket provenances for the other — each measured off what
 * the event actually holds rather than off what its vocabulary permits, so a
 * permanent control over a one-valued set never offers a tap that can only
 * close the door. The "change event" affordance likewise hides when this
 * device has nothing to change between.
 *
 * The mode row has the same gap one level up: Auto stops being the whole
 * truth the moment a ticket type is flagged verify-at-door, so the line
 * beneath it is the only place an operator sees that this door's Auto still
 * stops for a person. One such type reads as its own name, with nothing left
 * to open. Past one, the row itself is the control — one full-width line
 * carrying the phrase, the count, and the disclosure that opens the names —
 * because this ships on a door phone held one-handed, where a sentence plus a
 * separate button under the mode chips stacks three strata for one footnote
 * and the German of it cannot fit beside any affordance at that measure, and
 * because a per-day catalog's twenty-odd names printed inline would be a wall
 * between the mode chips and the filter below. It renders under Auto alone;
 * in Manual every admit is already confirmed by a person and there is no
 * distinction to draw.
 *
 * The accepted-types section carries two tiers, because the door's type
 * filter has two independent axes and a label reading only one of them is
 * wrong in half the states. The row above the band names what the set
 * FOLLOWS — the entry windows open at this instant, or the operator's own
 * pick — and the band's own master names how WIDE it is, since either axis
 * reaches the empty set that admits every type. Neither is called automatic
 * or manual: the check-in mode chips at the top of this panel own both words
 * for an unrelated axis, and one surface cannot carry two of them. While the
 * row follows the entry window, the band is a `readonly` readout at full
 * strength — the set an operator sees is the one the door is deriving, its
 * controls painted non-editable rather than hidden from assistive technology,
 * and no tap inside it can overwrite that silently. Turning the row off seeds
 * the pick from exactly that derived set, which is why the readout has to
 * stay legible rather than dim away.
 *
 * What holds the two tiers apart is a bordered tray around the band, authored
 * here rather than inside it so both voucher mounts stay exactly as they are.
 * It is an edge and never a tone: every ground and container fill this theme
 * carries sits within 1.17:1 of every other, so no pair of fills can hold a
 * boundary and a later change of tone would read as a fix while changing
 * nothing. The tray is identical in every state, because what it states is
 * rank and rank must not waver with a state signal. Its caption names what
 * the set inside it was drawn from — by the same rule the scan chrome names
 * it behind this overlay — and is where an operator watches `Jetzt gültig`
 * become their own stamped pick at the instant they flip the row above: the
 * one moment on this surface that teaches what following the entry window
 * means, and the reason the caption renders in both modes rather than only
 * the hand-set one.
 *
 * The two filters compose, and neither chip row shows the composition: each
 * reads plausible alone while the pair admits nobody. The admissible count
 * beneath them states that composition where the operator authors it, rather
 * than leaving a narrowed-to-nothing lane to be discovered one refused guest
 * at a time at the door.
 *
 * `eventName`/`canChangeEvent` are properties rather than a read off this
 * door's own event-selection state: that state is scanner-app-local (one
 * door's own event pick), so a component shared into the design studio's
 * fixtures too carries the door's event facts down as props instead.
 * The sign-out click only dispatches `signAction$`; resetting this door's
 * own event pick on sign-out is the scanner app's own reconciler's job, the
 * one place that still has the door's event-selection state in scope.
 *
 * The type-filter write is the one exception: it does read `selectedEventId`
 * directly, because saving the filter needs the real id it belongs to, not
 * a display string a prop can carry — settings.state.ts's own
 * `acceptedTypeIds` is what turns that recorded id into "does this filter
 * still apply."
 */
import { area } from "@mhmo91/schmancy";
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { computed } from "@mhmo91/schmancy/state";
import { type MessageKey, translate } from "@momo/i18n";
import { TicketType } from "@momo/types";
import { signAction$, signResult$ } from "@momo/views/auth";
import { html } from "lit";
import { property, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import {
	defer,
	EMPTY,
	exhaustMap,
	filter,
	finalize,
	merge,
	Subject,
	type Subscription,
	take,
	takeUntil,
	tap,
} from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
// Side-effect: registers <accepted-types-calendar>; the type rides beside it.
import "./accepted-types-calendar.view.ts";
import {
	eventDetailsById,
	selectedEventId,
	venueTimezone,
} from "../event/event.state.ts";
import {
	ticketTypeNames,
	ticketTypesIndex,
} from "../ticket-types/ticket-types.state.ts";
import { answeredTickets } from "../tickets/tickets.state.ts";
import type { AcceptedTypeRow } from "./accepted-types-calendar.view.ts";
import {
	acceptedTypeIds,
	type CheckinMode,
	checkinMode,
	hapticsEnabled,
	isTicketAccepted,
	manualPickedAt,
	type OriginFilter,
	originFilter,
	soundEnabled,
	typeFilter,
	typeFilterEventId,
	typeFilterIsAutomatic,
	typeFilterPickedAt,
} from "./settings.state.ts";
import { todayInstant } from "./today.state.ts";

// How many provenances THIS event's tickets actually carry — never the three
// members `OriginFilter` permits. An event sold entirely through the
// storefront holds one, and a filter row over a one-valued set offers a
// doorperson nothing but a tap that closes the door. A ticket minted before
// `origin` existed reads as `sale`, the same reading `isTicketAccepted`
// applies, so the row's gate and the filter it gates agree on what is here.
const distinctOriginCount = computed(() => {
	const origins = new Set<string>();
	for (const t of answeredTickets.get()?.byTicketId.values() ?? []) {
		origins.add(t.origin ?? "sale");
	}
	return origins.size;
});

// The types this event holds that a person confirms regardless of the mode
// chip. Read off `ticketTypeNames` rather than the raw map so the names come
// in the reader's own language and in the same order the type-filter chips
// below list them; each option keeps its id, which is what the disclosure
// past one type keys its rows on.
const alwaysConfirmedTypes = computed(() =>
	ticketTypeNames
		.get()
		.filter((o) => ticketTypesIndex.value.byId.get(o.id)?.verifyAtDoor),
);

// The event the band reads its windows against. Null until this door's own
// reconciler has answered, which is the same unresolved reading
// `acceptedTypeIds` fails open on.
const selectedEventDetail = computed(() => {
	const eventId = selectedEventId.value;
	if (eventId === null) return null;
	return eventDetailsById.value.get(eventId) ?? null;
});

// The band's rows. The effective entry window is resolved here, once, in epoch
// millis: the band derives every day and span from that pair and the zone, and
// a stored day beside the window would be a second representation free to
// contradict it. Names come through `ticketTypeNames` so the band lists what
// the chips listed, in the reader's own language and the same order.
const acceptedTypeRows = computed<readonly AcceptedTypeRow[]>(() => {
	const event = selectedEventDetail.get();
	if (event === null) return [];
	const timezone = venueTimezone.value;
	return ticketTypeNames.get().flatMap((option) => {
		const type = ticketTypesIndex.value.byId.get(option.id);
		if (!type) return [];
		const window = TicketType.resolveEntryWindow(type, event);
		return [
			{
				id: option.id,
				name: option.name,
				entryWindow: {
					opensAt: window.opensAt.toMillis(),
					closesAt: window.closesAt.toMillis(),
				},
				timezone,
			},
		];
	});
});

// The line the tray states over the set inside it: where that set came from,
// by the same rule the scan chrome's own caption uses, so the panel and the
// door never say different things. A door admitting every type came from no
// pick and no lane, so it names neither — the same rule that keeps a prefix
// off `Alle Ticketarten` at the door.
const trayCaption = computed<{
	readonly key: MessageKey;
	readonly pickedAt: number | null;
} | null>(() => {
	if (acceptedTypeIds.get().length === 0) return null;
	if (typeFilterIsAutomatic.get()) {
		return { key: "scanner.settings.validNow", pickedAt: null };
	}
	const picked = manualPickedAt.get();
	return picked === null
		? { key: "scanner.settings.ownSelection", pickedAt: null }
		: { key: "scanner.settings.ownSelectionAt", pickedAt: picked };
});

// Both axes composed, against the live index — the reading neither chip row
// can show on its own, because each row reads plausible while the pair admits
// nobody.
const admissibleTicketCounts = computed(() => {
	// No count before the ticket index has answered.
	const answered = answeredTickets.get();
	if (answered === null) return null;
	const accepted = acceptedTypeIds.get();
	const origin = originFilter.value;
	const tickets = answered.byTicketId;
	let admitted = 0;
	for (const t of tickets.values()) {
		if (isTicketAccepted(t, accepted, origin)) admitted += 1;
	}
	return { admitted, total: tickets.size };
});

@schmancyElement("checkin-settings-view")
export class SettingsView extends LocalizeMixin(SchmancyElement) {
	@property({ type: String }) eventName: string | null = null;
	@property({ type: Boolean }) canChangeEvent = false;

	// The disclosure while it is open. A 40px+ row under a gloved thumb takes a
	// double-tap, and each activation mounts its own overlay with its own
	// history entry — the stack cascades neither.
	private disclosure: Subscription | null = null;

	@state() private signingOut = false;
	@state() private signOutFailed = false;
	private readonly signOutRequested = new Subject<string>();

	override connectedCallback() {
		super.connectedCallback();
		this.signOutRequested
			.pipe(
				exhaustMap((requestId) =>
					merge(
						signResult$.pipe(
							filter(
								(result) =>
									result.requestId === requestId && result.kind === "signOut",
							),
							take(1),
							tap((result) => {
								this.signOutFailed = !result.ok;
								if (result.ok) this.close();
							}),
							finalize(() => {
								this.signingOut = false;
							}),
						),
						defer(() => {
							signAction$.next({ kind: "signOut", requestId });
							return EMPTY;
						}),
					),
				),
				takeUntil(this.disconnecting),
			)
			.subscribe();
	}

	private close(): void {
		this.dispatchEvent(
			new CustomEvent("close", { bubbles: true, composed: true }),
		);
	}

	override render() {
		const options = ticketTypeNames.get();
		const admissible = admissibleTicketCounts.get();
		const alwaysConfirmed = alwaysConfirmedTypes.get();
		const [onlyConfirmed] = alwaysConfirmed;
		const followsTheEntryWindow = typeFilterIsAutomatic.get();
		const caption = trayCaption.get();
		return html`
      <div
        ${overflowWithin()}
        class="flex max-h-[85vh] w-md max-w-full flex-col"
      >
        <div class="flex justify-end px-3 pt-3">
          <schmancy-icon-button
            data-overlay-close
            variant="text"
            title=${translate(this.uiLanguage, "scanner.settings.close")}
            aria-label=${translate(this.uiLanguage, "scanner.settings.close")}
            .icon=${"close"}
            @click=${() => this.close()}
          ></schmancy-icon-button>
        </div>

        <div class="flex flex-col gap-5 p-4 pt-0">
          <div class="flex flex-col gap-2">
            <schmancy-typography
              type="label"
              token="sm"
              class="text-surface-onVariant"
            >
              ${translate(this.uiLanguage, "scanner.settings.checkinMode.label")}
            </schmancy-typography>
            <!-- required for the same reason the origin row below carries it:
						     a single-mode chip container re-tapped on its already-selected
						     chip emits "", which would leave this door in neither auto nor
						     manual. required makes that re-tap a no-op. -->
            <schmancy-chips
              class="w-full"
              required
              .value=${checkinMode.value}
              @change=${(e: CustomEvent<{ value: string }>) => checkinMode.replace(e.detail.value as CheckinMode)}
            >
              <schmancy-filter-chip value="auto">
                <schmancy-icon>bolt</schmancy-icon>
                ${translate(this.uiLanguage, "scanner.settings.checkinMode.auto")}
              </schmancy-filter-chip>
              <schmancy-filter-chip value="manual">
                <schmancy-icon>touch_app</schmancy-icon>
                ${translate(this.uiLanguage, "scanner.settings.checkinMode.manual")}
              </schmancy-filter-chip>
            </schmancy-chips>
            ${when(
							checkinMode.value === "auto" && alwaysConfirmed.length > 0,
							() =>
								when(
									alwaysConfirmed.length === 1,
									() => html`
                    <schmancy-typography
                      type="label"
                      token="sm"
                      class="text-surface-onVariant"
                    >
                      ${translate(
												this.uiLanguage,
												"scanner.settings.alwaysConfirmedSingular",
												{
													names: onlyConfirmed?.name ?? "",
												},
											)}
                    </schmancy-typography>
                  `,
									() => html`
                    <schmancy-list-item
                      flush
                      aria-haspopup="dialog"
                      @click=${() => {
												if (this.disclosure?.closed === false) return;
												this.disclosure = show(
													() => {
														// Read inside the factory, not closed over from
														// render(): the overlay re-invokes this under its
														// own effect, so a catalog that changes while the
														// list is open moves the list too.
														const confirmed = alwaysConfirmedTypes.get();
														return html`
                              <div
                                class="flex max-h-[60svh] w-md max-w-full flex-col gap-3 p-5"
                              >
                                <div class="flex items-center gap-2">
                                  <schmancy-typography type="title" token="sm">
                                    ${translate(this.uiLanguage, "scanner.settings.alwaysConfirmedTitle")}
                                  </schmancy-typography>
                                  <schmancy-typography type="label" token="md" class="text-surface-onVariant">${confirmed.length}</schmancy-typography>
                                </div>
                                <schmancy-list
                                  ${overflowWithin()}
                                  surface="transparent"
                                >
                                  ${repeat(
																		confirmed,
																		(t) => t.id,
																		(t) => html`
                                      <schmancy-list-item readonly>
                                        <schmancy-typography
                                          type="body"
                                          token="md"
                                          >${t.name}</schmancy-typography
                                        >
                                      </schmancy-list-item>
                                    `,
																	)}
                                </schmancy-list>
                              </div>
                            `;
													},
													{
														opacity: "solid",
														ariaLabel: translate(
															this.uiLanguage,
															"scanner.settings.alwaysConfirmedTitle",
														),
													},
												)
													.pipe(takeUntil(this.disconnecting))
													.subscribe();
											}}
                    >
                      <schmancy-typography
                        class="flex min-h-11 items-center"
                        type="body"
                        token="md"
                      >
                        ${translate(this.uiLanguage, "scanner.settings.alwaysConfirmedTitle")}
                      </schmancy-typography>
                      <div slot="trailing" class="flex items-center gap-2">
                        <schmancy-typography type="label" token="md" class="text-surface-onVariant">${alwaysConfirmed.length}</schmancy-typography>
                        <schmancy-icon class="text-surface-onVariant"
                          >chevron_right</schmancy-icon
                        >
                      </div>
                    </schmancy-list-item>
                  `,
								),
						)}
          </div>

          <div class="flex flex-col gap-3">
            ${when(
							options.length >= 2,
							() => html`
                <div class="flex flex-col gap-3">
                  <div class="flex flex-col gap-2">
                    <schmancy-typography
                      type="label"
                      token="sm"
                      class="text-surface-onVariant"
                    >
                      ${translate(this.uiLanguage, "scanner.settings.acceptedTypes")}
                    </schmancy-typography>
                    <!-- What the set below follows, as a control of this panel and
										     not as a part of the band. The head-row anatomy it used to
										     wear — circular badge, uppercase wide-tracked label, the
										     band's own corner — is the band's, and worn one tier up it
										     bound the two switches into a pair and pushed the list they
										     both govern away, which is the inverse of the model. It
										     carries no badge, takes the panel's own contained-row corner
										     and inset, and states its predicate in sentence case,
										     because a predicate is not a head. Never Auto/Manuell: the
										     check-in mode chips at the top of this same panel already
										     own both words for an unrelated axis, and a door line
										     reading "Auto-Einlass · Automatisch" is two modes wearing
										     one word. -->
                    <schmancy-surface
                      type="containerLow"
                      class="flex items-center gap-3 rounded-2xl px-4 py-3"
                    >
                      <schmancy-typography
                        type="label"
                        token="lg"
                        class="min-w-0 flex-1 truncate"
                        aria-hidden="true"
                      >
                        ${translate(this.uiLanguage, "scanner.settings.goesByTheTimeOfDay")}
                      </schmancy-typography>
                      <schmancy-switch
                        label=${translate(this.uiLanguage, "scanner.settings.goesByTheTimeOfDayAria")}
                        ?checked=${followsTheEntryWindow}
                        @change=${(e: CustomEvent<{ value: boolean }>) => {
													// A change carrying the mode the door is already in is
													// not a mode change: without this it would re-date the
													// pick, or re-seed a set the door already enforces, on
													// any re-emission.
													if (e.detail.value === followsTheEntryWindow) return;
													if (e.detail.value) {
														// The stamp goes back with the event id: an instant
														// naming a set nobody enforces is a fact about nothing.
														typeFilterEventId.replace(null);
														typeFilterPickedAt.replace(null);
														return;
													}
													// The seed is read BEFORE the stamp below, because the
													// stamp flips the very gate this read passes through:
													// written first, acceptedTypeIds stops deriving and hands
													// back the dead typeFilter this door has not enforced
													// since it last switched events — resurrecting it and
													// stamping it live.
													const seed = acceptedTypeIds.get();
													typeFilter.replace([...seed]);
													typeFilterEventId.replace(selectedEventId.value);
													// What is recorded is the moment the operator acted, so the
													// clock is read at the gesture.
													typeFilterPickedAt.replace(Date.now());
												}}
                      ></schmancy-switch>
                    </schmancy-surface>
                  </div>
                  <!-- The tray: what states that the row above governs the band and
									     not the other way round. An edge and never a tone, because
									     every ground and container fill this theme carries sits within
									     1.17:1 of every other — no pair of fills can hold a boundary
									     here, so changing the tone later would read as a fix while
									     changing nothing. A ground tier supplies no glass hairline of
									     its own, which is what leaves this border heavier than the
									     edge of the pill it encloses, and one corner rung wider. It
									     is identical in every state: rank is not a state signal and
									     must not waver with one. -->
                  <schmancy-surface
                    type="containerLowest"
                    role="group"
                    aria-labelledby=${ifDefined(caption === null ? undefined : "accepted-types-drawn-from")}
                    class="flex flex-col gap-2 rounded-2xl border border-outline p-2"
                  >
                    ${when(
											followsTheEntryWindow || caption !== null,
											() => html`
                        <div class="flex items-center gap-1.5 px-1">
                          <!-- ⟳ stands in exactly the readings where this door
													     works the set out for itself and in none of the
													     operator's own, so the derivation is marked a
													     second time by something the palette cannot wash
													     out. It is the whole caption at a door that admits
													     everything, where nothing was drawn from and the
													     band's own master says the rest. -->
                          ${when(
														followsTheEntryWindow,
														() =>
															html`<schmancy-icon
                                class="text-label-sm text-surface-onVariant"
                                >autorenew</schmancy-icon
                              >`,
													)}
                          ${when(
														caption,
														(line) => html`
                              <schmancy-typography
                                id="accepted-types-drawn-from"
                                type="label"
                                token="sm"
                                class="min-w-0 truncate text-surface-onVariant"
                              >
                                ${translate(
																	this.uiLanguage,
																	line.key,
																	line.pickedAt === null
																		? undefined
																		: {
																				// An epoch instant, read by the operator who
																				// took it and standing where they were
																				// standing — so it renders in this device's
																				// own zone, with no timeZone option to move
																				// it somewhere nobody is.
																				time: new Date(
																					line.pickedAt,
																				).toLocaleTimeString(this.locale, {
																					hour: "2-digit",
																					minute: "2-digit",
																				}),
																			},
																)}
                              </schmancy-typography>
                            `,
													)}
                        </div>
                      `,
										)}
                    <accepted-types-calendar
                      .types=${acceptedTypeRows.get()}
                      .event=${selectedEventDetail.get()}
                      .selected=${acceptedTypeIds.get()}
                      .now=${todayInstant.value}
                      ?readonly=${followsTheEntryWindow}
                      @acceptedTypes=${(
												e: CustomEvent<{ value: readonly string[] }>,
											) => {
												// All three writes record the filter's own subject —
												// which event these typeIds belong to, and when the
												// operator picked them — so a later event switch is what
												// the gated read (acceptedTypeIds) needs to tell this
												// filter apart from a foreign one, and a chip tapped while
												// already picking re-times the pick.
												typeFilter.replace([...e.detail.value]);
												typeFilterEventId.replace(selectedEventId.value);
												typeFilterPickedAt.replace(Date.now());
											}}
                    ></accepted-types-calendar>
                  </schmancy-surface>
                </div>
              `,
						)}
            ${when(
							distinctOriginCount.get() >= 2,
							() => html`
                <div class="flex flex-col gap-2">
                  <schmancy-typography
                    type="label"
                    token="sm"
                    class="text-surface-onVariant"
                  >
                    ${translate(this.uiLanguage, "scanner.settings.origin.label")}
                  </schmancy-typography>
                  <!-- Single-select, and required on top of it: a single-mode
									     chips container emits value "" when the already-selected
									     chip is tapped again, and "" is no origin, so an idle tap
									     would leave this door refusing every ticket. required
									     makes that tap a no-op instead. Multi-select is wrong here
									     for the neighbouring reason — two chips render four states
									     over three meanings, where neither-selected and
									     both-selected would both have to mean admit-everything. -->
                  <schmancy-chips
                    required
                    class="w-full"
                    .value=${originFilter.value}
                    @change=${(e: CustomEvent<{ value: string }>) => originFilter.replace(e.detail.value as OriginFilter)}
                  >
                    <schmancy-filter-chip value="all">
                      ${translate(this.uiLanguage, "scanner.settings.origin.all")}
                    </schmancy-filter-chip>
                    <schmancy-filter-chip value="guest">
                      ${translate(this.uiLanguage, "scanner.settings.origin.guests")}
                    </schmancy-filter-chip>
                    <schmancy-filter-chip value="sale">
                      ${translate(this.uiLanguage, "scanner.settings.origin.sales")}
                    </schmancy-filter-chip>
                  </schmancy-chips>
                </div>
              `,
						)}

            ${when(
							admissible !== null,
							() => html`
                <schmancy-typography
                  type="label"
                  token="sm"
                  class="text-surface-onVariant"
                >
                  ${translate(this.uiLanguage, "scanner.settings.admits", {
										admitted: admissible?.admitted ?? 0,
										total: admissible?.total ?? 0,
									})}
                </schmancy-typography>
              `,
						)}
          </div>

          <div class="flex flex-col gap-3">
            <!-- The switch's label is ARIA-only; this row supplies visible
                             typography. Its change event reports user intent. -->
            <div class="flex items-center justify-between gap-3">
              <schmancy-typography type="body" token="md"
                >${translate(this.uiLanguage, "scanner.settings.sound")}</schmancy-typography
              >
              <schmancy-switch
                label=${translate(this.uiLanguage, "scanner.settings.sound")}
                ?checked=${soundEnabled.value}
                @change=${(e: CustomEvent<{ value: boolean }>) => {
									if (e.detail.value === soundEnabled.value) return;
									soundEnabled.replace(e.detail.value);
								}}
              ></schmancy-switch>
            </div>
            <div class="flex items-center justify-between gap-3">
              <schmancy-typography type="body" token="md"
                >${translate(this.uiLanguage, "scanner.settings.haptics")}</schmancy-typography
              >
              <schmancy-switch
                label=${translate(this.uiLanguage, "scanner.settings.haptics")}
                ?checked=${hapticsEnabled.value}
                @change=${(e: CustomEvent<{ value: boolean }>) => {
									if (e.detail.value === hapticsEnabled.value) return;
									hapticsEnabled.replace(e.detail.value);
								}}
              ></schmancy-switch>
            </div>
          </div>

          <div
            class="flex items-center justify-between gap-3 rounded-2xl bg-surface-containerLow px-4 py-3"
          >
            <div class="flex items-center gap-2">
              <schmancy-icon class="text-surface-onVariant"
                >door_front</schmancy-icon
              >
              <schmancy-typography type="body" token="sm"
                >${this.eventName ?? ""}</schmancy-typography
              >
            </div>
            ${when(
							this.canChangeEvent,
							() => html`
                <schmancy-button
                  variant="text"
                  @click=${() => {
										this.close();
										// The picker resumes any selected event, so release it before routing.
										selectedEventId.set(null);
										area.push({
											area: "root",
											component: "event-pick-view",
											historyStrategy: "push",
										});
									}}
                >
                  ${translate(this.uiLanguage, "scanner.session.changeEvent")}
                </schmancy-button>
              `,
						)}
          </div>

          <schmancy-button
            variant="text"
            color="error"
            .busy=${this.signingOut}
            @click=${() => {
							if (this.signingOut) return;
							this.signingOut = true;
							this.signOutRequested.next(crypto.randomUUID());
						}}
          >
            <schmancy-icon slot="prefix">logout</schmancy-icon>
            <span class="grid min-w-0">
              <span class="invisible col-start-1 row-start-1" aria-hidden="true">${translate(this.uiLanguage, "scanner.session.signOut")}</span>
              <span class="invisible col-start-1 row-start-1" aria-hidden="true">${translate(this.uiLanguage, "scanner.session.retrySignOut")}</span>
              <span class="col-start-1 row-start-1">${translate(this.uiLanguage, this.signOutFailed ? "scanner.session.retrySignOut" : "scanner.session.signOut")}</span>
            </span>
          </schmancy-button>
        </div>
      </div>
    `;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"checkin-settings-view": SettingsView;
	}
}
